import classNames from 'classnames';
import { useMemo } from 'react';

import { CollapsableTableProvider, useCollapsableTableContext } from './CollapsableTable.context';
import type {
  CollapsableTableCellRenderContext,
  CollapsableTableProps,
  CollapsableTableRenderApi,
  CollapsableTableTriggerProps,
} from './CollapsableTable.model';
import { DEFAULT_COLLAPSABLE_GROUP_ID } from './CollapsableTable.model';

import './CollapsableTable.scss';

export const CollapsableTable = <Row,>({ options }: CollapsableTableProps<Row>) => {
  const initialExpandedByGroup = useMemo(() => options.initialExpandedByGroup ?? {}, [options.initialExpandedByGroup]);
  return (
    <CollapsableTableProvider initialExpandedByGroup={initialExpandedByGroup}>
      <CollapsableTableContent options={options} />
    </CollapsableTableProvider>
  );
};

export const CollapsableTableTrigger = ({ groupId, children }: CollapsableTableTriggerProps) => {
  const { isGroupExpanded, setGroupExpanded, toggleGroup } = useCollapsableTableContext();
  const resolvedGroupId = groupId ?? DEFAULT_COLLAPSABLE_GROUP_ID;
  const isExpanded = isGroupExpanded(resolvedGroupId);

  return children({
    isExpanded,
    setExpanded: isExpandedValue => setGroupExpanded(resolvedGroupId, isExpandedValue),
    toggle: () => toggleGroup(resolvedGroupId),
  });
};

const CollapsableTableContent = <Row,>({ options }: CollapsableTableProps<Row>) => {
  const { rows, columns, getRowKey, tableClassName, tableE2E } = options;
  const { isGroupExpanded, setGroupExpanded, toggleGroup } = useCollapsableTableContext();
  const columnsWithDefaultGroupId = useMemo(
    () =>
      columns.map(column => ({
        ...column,
        groupId: column.groupId ?? DEFAULT_COLLAPSABLE_GROUP_ID,
      })),
    [columns],
  );

  const api: CollapsableTableRenderApi = useMemo(
    () => ({
      isGroupExpanded,
      setGroupExpanded,
      toggleGroup,
    }),
    [isGroupExpanded, setGroupExpanded, toggleGroup],
  );

  const visibleColumns = useMemo(
    () => columnsWithDefaultGroupId.filter(column => column.isVisible?.(api) ?? true),
    [columnsWithDefaultGroupId, api],
  );

  return (
    <table
      className={classNames('table table-sm table-bordered mb-0 collapsable-table', tableClassName)}
      data-e2e={tableE2E}
    >
      <thead>
        <tr>
          {visibleColumns.map(column =>
            (() => {
              const isExpanded = column.isCollapsable ? api.isGroupExpanded(column.groupId) : false;

              return (
                <th
                  key={column.id}
                  className={classNames(
                    'collapsable-cell',
                    {
                      'collapsable-column': column.isCollapsable,
                      'is-expanded': isExpanded,
                    },
                    typeof column.headerClassName === 'function' ? column.headerClassName(api) : column.headerClassName,
                  )}
                >
                  {column.header}
                </th>
              );
            })(),
          )}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, rowIndex) => {
          const key = getRowKey?.(row, rowIndex) ?? rowIndex;
          return (
            <tr key={key}>
              {visibleColumns.map(column => {
                const context: CollapsableTableCellRenderContext<Row> = {
                  row,
                  rowIndex,
                  api,
                };
                const isExpanded = column.isCollapsable ? api.isGroupExpanded(column.groupId) : false;

                return (
                  <td
                    key={`${column.id}-${key}`}
                    className={classNames(
                      {
                        'collapsable-column': column.isCollapsable,
                        'is-expanded': isExpanded,
                      },
                      typeof column.cellClassName === 'function' ? column.cellClassName(context) : column.cellClassName,
                    )}
                  >
                    {column.renderCell(context)}
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};
